(() => {
  'use strict';
  if (window.EilSearchQuery) return;

  class EilSearchQuery {
    static skuCache = new Map();
    static nativeCounts = new Map();
    static pendingLookups = new Map();
    static submissions = new WeakMap();
    static cacheLifetime = 180000;

    static normalize(value = '') {
      return String(value).normalize('NFC').trim().replace(/\s+/gu, ' ').toLowerCase();
    }

    // Ordinary text always keeps Shopify's native matching and synonyms.
    static build(value = '') {
      return EilSearchQuery.normalize(value);
    }

    static skuQuery(value) {
      const escaped = EilSearchQuery.normalize(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `variants.sku:"${escaped}"`;
    }

    static cacheKey(value, unavailable = 'last') {
      return `${unavailable}:${EilSearchQuery.normalize(value)}`;
    }

    static resultQuery(value, unavailable = 'last') {
      const term = EilSearchQuery.normalize(value);
      const cached = EilSearchQuery.skuCache.get(EilSearchQuery.cacheKey(term, unavailable));
      const native = EilSearchQuery.nativeCounts.get(EilSearchQuery.cacheKey(term, unavailable));
      return cached && cached.expires > Date.now() && (cached.products.some(product => product._eilExactSku)
          || (cached.products.length && native?.expires > Date.now() && native.count === 0))
        ? EilSearchQuery.skuQuery(term) : term;
    }

    static async lookupSku(value, { endpoint = '/search/suggest.json', unavailable = 'last' } = {}) {
      const term = EilSearchQuery.normalize(value);
      if (!term) return [];
      const key = EilSearchQuery.cacheKey(term, unavailable);
      const cached = EilSearchQuery.skuCache.get(key);
      if (cached && cached.expires > Date.now()) return cached.products;
      if (EilSearchQuery.pendingLookups.has(key)) return EilSearchQuery.pendingLookups.get(key);

      const lookup = (async () => {
        const url = new URL(endpoint.replace(/\/suggest\.json$/, ''), window.location.href);
        url.searchParams.set('q', EilSearchQuery.skuQuery(term));
        url.searchParams.set('type', 'product');
        url.searchParams.set('view', 'sku-lookup');
        url.searchParams.set('options[unavailable_products]', unavailable);
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 3000);
        try {
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`SKU lookup HTTP ${response.status}`);
          const html = await response.text();
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-eil-sku-results]');
          const data = node ? JSON.parse(node.textContent) : null;
          const products = data?.products?.map(product => ({
            ...product, _eilExactSku: product.skus.some(sku => EilSearchQuery.normalize(sku) === term)
          }));
          if (!Array.isArray(products)) throw new Error('Invalid SKU lookup response');
          if (EilSearchQuery.skuCache.size >= 100) {
            EilSearchQuery.skuCache.delete(EilSearchQuery.skuCache.keys().next().value);
          }
          EilSearchQuery.skuCache.set(key, { products, expires: Date.now() + EilSearchQuery.cacheLifetime });
          return products;
        } finally {
          window.clearTimeout(timer);
        }
      })();
      EilSearchQuery.pendingLookups.set(key, lookup);
      try { return await lookup; }
      finally { EilSearchQuery.pendingLookups.delete(key); }
    }

    static rememberNative(value, products, unavailable = 'last') {
      const key = EilSearchQuery.cacheKey(value, unavailable);
      if (EilSearchQuery.nativeCounts.size >= 100) EilSearchQuery.nativeCounts.delete(EilSearchQuery.nativeCounts.keys().next().value);
      EilSearchQuery.nativeCounts.set(key, { count: products.length, expires: Date.now() + EilSearchQuery.cacheLifetime });
    }

    static async resolveQuery(value, unavailable = 'last') {
      const term = EilSearchQuery.normalize(value);
      const products = await EilSearchQuery.lookupSku(term, { unavailable });
      const key = EilSearchQuery.cacheKey(term, unavailable);
      if (products.length && !products.some(product => product._eilExactSku) && !(EilSearchQuery.nativeCounts.get(key)?.expires > Date.now())) {
        const url = new URL('/search/suggest.json', window.location.href);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[options][unavailable_products]', unavailable);
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 3000);
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Native search unavailable');
          const data = await response.json();
          EilSearchQuery.rememberNative(term, data.resources.results.products, unavailable);
        } finally { window.clearTimeout(timer); }
      }
      return EilSearchQuery.resultQuery(term, unavailable);
    }

    static legacyTerm(query) {
      const marker = ') OR variants.sku:"';
      const index = query.lastIndexOf(marker);
      if (index < 0 || !query.endsWith('"')) return null;
      const term = query.slice(index + marker.length, -1).replace(/\\(["\\])/g, '$1');
      if (EilSearchQuery.normalize(term) !== term) return null;
      const escape = text => text.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const title = term.split(' ').filter(word => /[\p{L}\p{N}]/u.test(word))
        .map(word => `title:"${escape(word)}"`).join(' AND ');
      const suffix = EilSearchQuery.skuQuery(term);
      return query === `(${title}) OR ${suffix}` || query === `(${term}) OR ${suffix}` ? term : null;
    }

    static async migrateLegacyUrl() {
      const url = new URL(window.location.href);
      if (!url.pathname.replace(/\/$/, '').endsWith('/search')) return;
      const term = EilSearchQuery.legacyTerm(url.searchParams.get('q') || '');
      if (term === null) return;
      let query = term;
      try { query = await EilSearchQuery.resolveQuery(term, url.searchParams.get('options[unavailable_products]') || 'last'); }
      catch (_) { /* Keep the native query if the SKU lookup is unavailable. */ }
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.delete('options[fields]');
      window.location.replace(url.href);
    }

    static isSearchForm(form) {
      return form instanceof HTMLFormElement
        && new URL(form.action, window.location.href).pathname.replace(/\/$/, '').endsWith('/search');
    }

    static onFormData(event) {
      const form = event.target;
      if (!EilSearchQuery.isSearchForm(form) || !event.formData.has('q')) return;
      const term = EilSearchQuery.normalize(event.formData.get('q'));
      const submission = EilSearchQuery.submissions.get(form);
      event.formData.set('q', submission?.term === term ? submission.query : term);
      // Use Shopify's default fields, including descriptions and synonyms.
      event.formData.delete('options[fields]');
      if (!event.formData.has('type')) event.formData.set('type', 'product');
      if (!event.formData.has('options[prefix]')) event.formData.set('options[prefix]', 'last');
    }

    static async onSubmit(event) {
      const form = event.target;
      if (event.defaultPrevented || !EilSearchQuery.isSearchForm(form)) return;
      const input = form.elements.namedItem('q');
      if (!input || !('value' in input)) return;
      const term = EilSearchQuery.normalize(input.value);
      if (!term) return;
      event.preventDefault();
      if (EilSearchQuery.submissions.has(form)) return;
      const unavailable = form.elements.namedItem('options[unavailable_products]')?.value || 'last';
      const submission = { term, query: term };
      EilSearchQuery.submissions.set(form, submission);
      const previousBusy = form.getAttribute('aria-busy');
      form.setAttribute('aria-busy', 'true');
      try {
        try {
          submission.query = await EilSearchQuery.resolveQuery(term, unavailable);
        } catch (_) {
          // Network failure must never prevent a normal Shopify search.
        }
        if (EilSearchQuery.normalize(input.value) !== term || !form.isConnected) return;
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        EilSearchQuery.submissions.delete(form);
        if (previousBusy === null) form.removeAttribute('aria-busy');
        else form.setAttribute('aria-busy', previousBusy);
      }
    }
  }

  window.EilSearchQuery = EilSearchQuery;
  document.addEventListener('formdata', EilSearchQuery.onFormData);
  document.addEventListener('submit', EilSearchQuery.onSubmit);
  EilSearchQuery.migrateLegacyUrl();
})();

